<template>
    <DetailCard :title="$t('lengthGroups')">
      <template #title.append>
        <v-row no-gutters justify="end">
          <Icon
          v-if="crud"
          icon="mdi-plus"
          :tooltipText="$t('new')"
          @icon-clicked="newLengthGroupClicked()"
          margin=""
          :small="false"
          tabindex="0"/>
        </v-row>
      </template>

      <ShortcutWrapper @n-shortcut="onNShortcut" isDialog>
        <v-data-table
        class="small-headers"
        :loading="loading"
        dense
        :items="lengthGroupItems"
        :items-per-page="-1"
        hide-default-footer
        fixed-header
        :height="tableHeight"
        :headers="lengthGroupHeaders">

          <template #item.logLengths="{ item }">
            <v-row no-gutters class="pb-1">
              <v-col cols="auto"
              v-for="(len, idx) in item.logLengths"
              :key="idx">
                <v-chip
                label
                outlined
                class="mt-1 mr-1"
                :ripple="false"
                :class="selectedLengths.includes(len.length) ? 'selected-length' : 'grey-chip'">
                  <span>
                    {{ `${parseInt(len.length)}'` }}
                  </span>
                </v-chip>
              </v-col>
            </v-row>
          </template>

          <template #item.actions="{ item }">
            <v-row v-if="!!item.label" no-gutters justify="end">
              <Icon
              icon="mdi-pencil"
              :tooltipText="$t('edit')"
              @icon-clicked="editLengthGroupClicked(item)"/>
              <Icon
              icon="mdi-delete-forever"
              :tooltipText="$t('delete')"
              @icon-clicked="deleteLengthGroupClicked(item)"
              margin=""/>
            </v-row>
          </template>
        </v-data-table>
      </ShortcutWrapper>

      <Dialog
      :stateId="dialogId"
      @dialog-closing="handleCancelEdit"
      @click:outside="handleCancelEdit"
      @close="handleCancelEdit">
        <AccountLengthGroupForm
        :lengthGroup="focusedLengthGroup"
        @save="handleSaveLengthGroup"
        @cancel="handleCancelEdit"/>
      </Dialog>
    </DetailCard>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { lengthGroupHeaders } from '@/headers/Account'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'

export default {
  name: 'AccountLengthGroups',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    AccountLengthGroupForm: () => import('./AccountLengthGroupForm.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },

    accountId: {
      type: Number,
      required: false,
      default: undefined
    },

    selectedLengths: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  async mounted () {
    await this.refresh()
  },

  data: () => ({
    dialogId: uniqueDialogId('account-length-group'),
    loading: true,
    focusedLengthGroup: null
  }),

  computed: {
    ...mapGetters('account-scaling', ['accountLengthGroups']),
    ...mapGetters('dialog', ['topDialogState', 'dialogStateById']),

    tableHeight () {
      const { fullscreen } = this.topDialogState
      const dialogHeightPcnt = fullscreen ? 100 : 90
      const dialogBufferSpace = this.$vuetify.breakpoint.xl ? 350 : 500
      return `max(${256}px, min(${800}px, calc(${dialogHeightPcnt}vh - ${dialogBufferSpace}px)))`
    },

    lengthGroupHeaders () {
      return lengthGroupHeaders(this.crud)
    },

    editingLengthGroup () {
      return this.accountLengthGroups.some(lg => lg.editing)
    },

    creatingLengthGroup () {
      return this.accountLengthGroups.some(lg => lg.editing && !lg.label && !(lg.logLengths?.length > 0))
    },

    lengthGroupItems () {
      return this.accountLengthGroups.filter(lg => lg.label && lg.logLengths?.length > 0)
    }
  },

  methods: {
    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'createAccountLengthGroup',
      'updateAccountLengthGroup', 'deleteAccountLengthGroup'
    ]),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async refresh () {
      this.loading = true
      if (this.accountId) {
        await this.fetchLengthGroups()
      }
      this.loading = false
    },

    async fetchLengthGroups () {
      await this.fetchAccountLengthGroups(this.accountId)
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: DialogWidth.SimpleWide })
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    newLengthGroupClicked () {
      this.focusedLengthGroup = null
      this.openDialog()
    },

    editLengthGroupClicked (lengthGroup) {
      this.focusedLengthGroup = lengthGroup
      this.openDialog()
    },

    async deleteLengthGroupClicked (lengthGroup) {
      await this.removeLengthGroupOnServer(lengthGroup)
    },

    async removeLengthGroupOnServer (lengthGroup) {
      await this.deleteAccountLengthGroup({ accountId: this.accountId, lengthGroupId: lengthGroup.lengthGroupId })
      this.emitLengthGroupMutated()
      await this.fetchLengthGroups()
    },

    handleCancelEdit () {
      this.focusedLengthGroup = null
      this.closeDialog()
    },

    async handleSaveLengthGroup (lengthGroup) {
      if (lengthGroup.lengthGroupId) {
        await this.updateLengthGroupOnServer(lengthGroup)
      } else {
        await this.createLengthGroupOnServer(lengthGroup)
      }
      this.closeDialog()
      await this.fetchLengthGroups()
    },

    async createLengthGroupOnServer (lengthGroup) {
      await this.createAccountLengthGroup({
        accountId: this.accountId,
        logLengthIds: lengthGroup.logLengths.map(l => l.logLengthId),
        ...lengthGroup
      })
      this.emitLengthGroupMutated()
    },

    async updateLengthGroupOnServer (lengthGroup) {
      await this.updateAccountLengthGroup({
        accountId: this.accountId,
        logLengthIds: lengthGroup.logLengths.map(l => l.logLengthId),
        ...lengthGroup
      })
      this.emitLengthGroupMutated()
    },

    onNShortcut () {
      if (this.childDialogIsOpen()) {
        return
      }
      this.newLengthGroupClicked()
    },

    childDialogIsOpen () {
      return !!this.dialogStateById(this.dialogId)
    },

    emitLengthGroupMutated () {
      this.$emit('account-length-group-mutated')
    }
  }
}
</script>

<style scoped>
  .v-chip.v-chip--outlined.selected-length {
    background-color: #d15f27 !important;
    color: #fff !important;
  }
</style>
